import { ReactElement } from "react";
import { useSearchParams } from "react-router";
import { useDebounce } from "use-debounce";
import { useShallow } from "zustand/react/shallow";
import { useStorefrontTagList } from "../../../../../hooks/api/v2/storefrontTags/useStorefrontTagList";
import { useSellarCurrentBusiness } from "../../../../../hooks/useSellarCurrentBusiness.shared";
import { abvFilterType } from "../../../../discovery/v2/DiscoveryV2SearchStore";
import { useStorefrontFilterStore } from "../../../StorefrontFilterStore";
import { useStockListContext } from "../../../useStockListContext";
import { StorefrontProductTagsContent } from "./StorefrontProductTagsContent";

export const StorefrontProductTags = (): ReactElement | null => {
  const { supplierQuery, unAuthorisedUserPostcode } = useStockListContext();
  const { business } = useSellarCurrentBusiness();

  const {
    selectedTags,
    selectedPriceTier,
    selectedAvailabilityTier,
    searchValue,
    selectedGroupIds,
    selectedAbvTypes,
    selectedDietaryTypes,
  } = useStorefrontFilterStore(
    useShallow((state) => ({
      selectedTags: state.selectedTags,
      selectedPriceTier: state.selectedPriceTier,
      selectedAvailabilityTier: state.selectedAvailabilityTier,
      searchValue: state.searchValue,
      selectedGroupIds: state.selectedGroupIds,
      selectedAbvTypes: state.selectedAbvTypes,
      selectedDietaryTypes: state.selectedDietaryTypes,
    })),
  );

  const [debouncedSearchValue] = useDebounce(searchValue, 500);
  const [searchParams] = useSearchParams();

  const storefrontLink =
    searchParams.get("storefrontLink") || searchParams.get("priceTierLink");

  const tagsQuery = useStorefrontTagList(
    {
      query: [
        {
          type: "SupplierId",
          value: parseInt(supplierQuery.data?.id.toString() ?? ""),
          modifier: "EQUALS",
        },
        {
          type: "PriceTierId",
          value:
            business?.Supplier?.id === supplierQuery.data?.id &&
            selectedPriceTier
              ? selectedPriceTier
              : 0,
          modifier: "EQUALS",
        },
        {
          type: "AvailabilityTierId",
          value:
            business?.Supplier?.id === supplierQuery.data?.id &&
            selectedAvailabilityTier
              ? selectedAvailabilityTier
              : 0,
          modifier: "EQUALS",
        },
        {
          type: "isPreview",
          value:
            !!business?.Supplier &&
            business.Supplier.id === supplierQuery.data?.id,
          modifier: "EQUALS",
        },
        {
          type: "storefrontLink",
          value: storefrontLink ?? "",
          modifier: "EQUALS",
        },
        ...(unAuthorisedUserPostcode
          ? ([
              {
                type: "postcode",
                value: unAuthorisedUserPostcode,
                modifier: "EQUALS",
              },
            ] as const)
          : []),
        ...(debouncedSearchValue
          ? ([
              {
                type: "search",
                value: debouncedSearchValue,
                modifier: "EQUALS",
              },
            ] as const)
          : []),
        ...(selectedTags.length
          ? ([
              {
                type: "types",
                value: selectedTags,
                modifier: "IN",
              },
            ] as const)
          : []),
        ...(selectedGroupIds.length
          ? ([
              {
                type: "id",
                value: selectedGroupIds,
                modifier: "IN",
              },
            ] as const)
          : []),
        ...(selectedAbvTypes.length
          ? selectedAbvTypes.map((type) => abvFilterType[type].query)
          : []),
        ...(selectedDietaryTypes.length
          ? ([
              {
                type: "dietaryType",
                modifier: "IN",
                value: Array.from(selectedDietaryTypes.values()),
              },
            ] as const)
          : []),
      ],
    },
    {
      enabled: !!supplierQuery.data?.id,
    },
  );

  return (
    <StorefrontProductTagsContent
      supplier={supplierQuery.data}
      tagsQuery={tagsQuery}
      selectedGroupIds={selectedGroupIds}
    />
  );
};
